import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Loader2,
  Check,
  ArrowRight,
  ArrowLeft,
  Wrench,
  Package,
  X,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "@/lib/router-compat";

type BusinessType = "servicio" | "productos" | "descarte" | "";
type SubType =
  "residencial" | "comercial" | "ambos" | "final" | "negocios" | "";

export function FunnelForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [businessType, setBusinessType] = useState<BusinessType>("");
  const [subType, setSubType] = useState<SubType>("");
  const [businessStatus, setBusinessStatus] = useState("");
  const [whenToStart, setWhenToStart] = useState("");
  const [monthlyRevenue, setMonthlyRevenue] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const STEPS = 8;

  const previousStepRef = useRef(step);
  useEffect(() => {
    if (previousStepRef.current !== step) {
      previousStepRef.current = step;
      if (containerRef.current) {
        containerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [step]);

  // Pregunta 1b: ¿con quién trabajás / a quién le vendés?
  const showSubQuestion = step === 2 && businessType !== "descarte";

  // El total de "pasos visibles" depende de si hay subpregunta (descarte no la tiene, pero descarte = paso final)
  const isDescarte =
    businessType === "descarte" || businessStatus === "Recién estoy arrancando";

  // Número de paso que ve el usuario (la subpregunta es parte del flujo de 1b)
  const displayStep = step;

  const canContinue = () => {
    if (step === 1 && !businessType) return false;
    if (step === 2 && businessType !== "descarte" && !subType) return false;
    if (step === 3 && !businessStatus) return false;
    if (step === 4 && !whenToStart) return false;
    if (step === 5 && !monthlyRevenue) return false;
    if (step === 6 && (!firstName || !lastName || !companyName)) return false;
    if (step === 7 && (!phone || !email)) return false;
    return true;
  };

  const handleContinue = () => {
    if (!canContinue()) return;

    // Si en paso 1 eligió descarte, saltar a paso final de "no aplica"
    if (step === 1 && businessType === "descarte") {
      // descarte directo: no se llama
      import("@/lib/analytics").then(({ trackFunnelDescarte }) =>
        trackFunnelDescarte("No es proveedor de servicios"),
      );
      navigate("/te-vamos-a-llamar?d=1");
      return;
    }

    if (step < STEPS) {
      import("@/lib/analytics").then(({ trackFunnelStep }) =>
        trackFunnelStep(step, `Pregunta ${step}`),
      );
      setStep(step + 1);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isSubmitting) return;

    if (step < STEPS) {
      handleContinue();
      return;
    }

    if (!phone || !email) return;

    // Guard: si no es descarte, el subtipo (a quién le vende) es obligatorio
    if (businessType !== "descarte" && !subType) {
      setStep(2);
      return;
    }

    setIsSubmitting(true);
    try {
      // Descarte tardío: recién arrancando
      if (isDescarte) {
        navigate("/te-vamos-a-llamar?d=1");
        return;
      }

      // Normalizar teléfono
      let cleanPhone = phone.replace(/[\s-]/g, "");
      if (cleanPhone.startsWith("+5411")) {
        cleanPhone = "+54911" + cleanPhone.slice(5);
      } else if (
        cleanPhone.startsWith("+54") &&
        !cleanPhone.startsWith("+549")
      ) {
        cleanPhone = "+549" + cleanPhone.slice(3);
      } else if (!cleanPhone.startsWith("+")) {
        cleanPhone = "+549" + cleanPhone;
      }

      // Cookies Meta
      const getCookie = (name: string) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(";").shift() || "";
        return "";
      };

      let fbc = getCookie("_fbc");
      const fbp = getCookie("_fbp");
      const fbclid = localStorage.getItem("fbclid");
      if (!fbc && fbclid) {
        fbc = `fb.1.${Date.now()}.${fbclid}`;
      }

      const eventId = `lead-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      // Texto compuesto de tipo de negocio
      const tipoNegocioTexto =
        businessType === "servicio"
          ? `Ofrezco un servicio - ${subType === "residencial" ? "Particulares (residencial)" : subType === "comercial" ? "Empresas (comercial)" : "Ambos"}`
          : `Vendo productos - ${subType === "final" ? "Al consumidor final" : subType === "negocios" ? "Solo a otros negocios" : "A ambos"}`;

      const trackingPayload = {
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: "funnel demo v3",
        formData: {
          first_name: firstName,
          last_name: lastName,
          email,
          phone: cleanPhone,
          country: "Argentina",
          "contact.nombre_de_tu_empresa": companyName,
          "contact.tipo_de_negocio":
            businessType === "servicio"
              ? "Ofrezco un servicio"
              : "Vendo productos",
          "contact.a_quien_le_vende":
            subType === "residencial"
              ? "Particulares (residencial)"
              : subType === "comercial"
                ? "Empresas (comercial)"
                : subType === "final"
                  ? "Al consumidor final"
                  : subType === "negocios"
                    ? "Solo a otros negocios"
                    : "Ambos",
          "contact.como_viene_el_negocio": businessStatus,
          "contact.para_cuando_lo_queres_resolver": whenToStart,
          "contact.facturacion_mensual_usd": monthlyRevenue,
          "contact.event_id": eventId,
          ...(fbc ? { "contact.fbc": fbc } : {}),
          ...(fbp ? { "contact.fbp": fbp } : {}),
          ...(localStorage.getItem("fbclid")
            ? { fbclid: localStorage.getItem("fbclid") }
            : {}),
          "contact.utm_source": localStorage.getItem("utm_source") || "",
          "contact.utm_medium": localStorage.getItem("utm_medium") || "",
          "contact.utm_campaign": localStorage.getItem("utm_campaign") || "",
          "contact.utm_content": localStorage.getItem("utm_content") || "",
          "contact.utm_term": localStorage.getItem("utm_term") || "",
          "contact.angulo": localStorage.getItem("a") || "",
          "contact.url_origen":
            localStorage.getItem("url_origen") || window.location.href,
          "contact.country": "Argentina",
          "contact.address_country": "Argentina",
        },
        formLabels: {
          first_name: "Nombre",
          last_name: "Apellido",
          email: "Email",
          phone: "Teléfono",
          "contact.nombre_de_tu_empresa": "Nombre de tu empresa",
          "contact.tipo_de_negocio": "Tipo de negocio",
          "contact.a_quien_le_vende": "A quién le vende",
          "contact.como_viene_el_negocio": "Cómo viene el negocio",
          "contact.para_cuando_lo_queres_resolver":
            "Para cuándo lo querés resolver",
          "contact.facturacion_mensual_usd": "Facturación mensual (USD)",
        },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_ce8ad559ba324d4b86a52e0b92308640",
        locationId: "UmeUICnWy44Ung9XrMjl",
        sessionId: crypto.randomUUID(),
      };

      await fetch(
        "https://backend.leadconnectorhq.com/external-tracking/events",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            version: "2021-07-28",
          },
          body: JSON.stringify(trackingPayload),
        },
      ).catch(() => {});

      // Guardar el event_id para que /te-vamos-a-llamar dispare el Lead en page view
      localStorage.setItem("lead_event_id", eventId);

      window.location.href = `/te-vamos-a-llamar?n=${encodeURIComponent(firstName)}`;
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Error al enviar",
        description:
          "Hubo un problema al procesar tu solicitud. Por favor intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRadioOptions = (
    field: string,
    options: { value: string; label: string; icon?: any }[],
    currentValue: string,
    onSelect: (value: string) => void,
    autoAdvance: boolean = true,
    stepNum: number = step,
  ) => {
    return (
      <div className="space-y-3 mt-2">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = currentValue === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onSelect(option.value);
                if (autoAdvance) {
                  setTimeout(() => {
                    import("@/lib/analytics").then(({ trackFunnelStep }) =>
                      trackFunnelStep(stepNum, `Pregunta ${stepNum}`),
                    );
                    // Si eligió descarte en paso 1, redirigir (no avanzar al paso 2)
                    if (stepNum === 1 && option.value === "descarte") {
                      import("@/lib/analytics").then(
                        ({ trackFunnelDescarte }) =>
                          trackFunnelDescarte("No es proveedor de servicios"),
                      );
                      navigate("/te-vamos-a-llamar?d=1");
                      return;
                    }
                    setStep(stepNum + 1);
                  }, 350);
                }
              }}
              className={`w-full flex items-center p-4 rounded-xl border transition-all text-left ${
                isSelected
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-foreground border-white/10 hover:border-white/20"
              }`}
            >
              {Icon ? (
                <Icon
                  className={`w-5 h-5 mr-3 flex-shrink-0 ${isSelected ? "text-primary-foreground" : "text-muted-foreground"}`}
                />
              ) : isSelected ? (
                <Check className="w-5 h-5 mr-3 flex-shrink-0" />
              ) : (
                <div className="w-5 h-5 mr-3 flex-shrink-0" />
              )}
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full" ref={containerRef}>
      <div className="mb-2 pb-6 border-b">
        <div className="mt-4">
          <Progress
            value={Math.round((step / STEPS) * 100)}
            className="h-2.5 transition-all duration-500 ease-out"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                Avanzando
              </span>
            </p>
            <p className="text-xs font-medium text-foreground tabular-nums">
              {Math.round((step / STEPS) * 100)}%
            </p>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-4 min-h-[300px] flex flex-col justify-between"
      >
        <div>
          {/* STEP 1 — ¿Cómo es tu negocio? */}
          {step === 1 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <Label className="text-lg font-medium mb-3 block">
                ¿Cómo es tu negocio? *
              </Label>
              {renderRadioOptions(
                "businessType",
                [
                  {
                    value: "servicio",
                    label: "Ofrezco un servicio (voy, hago el trabajo, cotizo)",
                    icon: Wrench,
                  },
                  {
                    value: "productos",
                    label:
                      "Vendo productos (soy distribuidor, importador o mayorista)",
                    icon: Package,
                  },
                  { value: "descarte", label: "Ninguna de las dos", icon: X },
                ],
                businessType,
                (val) => setBusinessType(val as BusinessType),
                true,
                1,
              )}
            </div>
          )}

          {/* STEP 2 — Subpregunta condicional */}
          {showSubQuestion && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <Label className="text-lg font-medium mb-3 block">
                {businessType === "servicio"
                  ? "¿Con quién trabajás? *"
                  : "¿A quién le vendés? *"}
              </Label>
              {renderRadioOptions(
                "subType",
                businessType === "servicio"
                  ? [
                      {
                        value: "residencial",
                        label: "Particulares (residencial)",
                      },
                      { value: "comercial", label: "Empresas (comercial)" },
                      { value: "ambos", label: "Ambos" },
                    ]
                  : [
                      { value: "final", label: "Al consumidor final" },
                      { value: "negocios", label: "Solo a otros negocios" },
                      { value: "ambos", label: "A ambos" },
                    ],
                subType,
                (val) => setSubType(val as SubType),
                true,
                2,
              )}
            </div>
          )}

          {/* STEP 3 — ¿Cómo viene el negocio? */}
          {step === 3 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <Label className="text-lg font-medium mb-3 block">
                ¿Cómo viene el negocio? *
              </Label>
              {renderRadioOptions(
                "businessStatus",
                [
                  {
                    value: "Lento, necesito más trabajos ahora",
                    label: "Lento, necesito más trabajos ahora",
                  },
                  {
                    value: "Bien, pero no es constante",
                    label: "Bien, pero no es constante",
                  },
                  {
                    value: "A full, tengo la agenda llena",
                    label: "A full, tengo la agenda llena",
                  },
                  {
                    value: "Recién estoy arrancando",
                    label: "Recién estoy arrancando",
                  },
                ],
                businessStatus,
                setBusinessStatus,
                true,
                3,
              )}
            </div>
          )}

          {/* STEP 4 — ¿Para cuándo lo querés resolver? */}
          {step === 4 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <Label className="text-lg font-medium mb-3 block">
                ¿Para cuándo lo querés resolver? *
              </Label>
              {renderRadioOptions(
                "whenToStart",
                [
                  { value: "Lo antes posible", label: "Lo antes posible" },
                  {
                    value: "El mes que viene o el otro",
                    label: "El mes que viene o el otro",
                  },
                  {
                    value: "Más adelante este año",
                    label: "Más adelante este año",
                  },
                  {
                    value: "Solo estoy viendo opciones",
                    label: "Solo estoy viendo opciones",
                  },
                ],
                whenToStart,
                setWhenToStart,
                true,
                4,
              )}
            </div>
          )}

          {/* STEP 5 — Facturación mensual USD */}
          {step === 5 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <Label className="text-lg font-medium mb-3 block">
                ¿Cuánto facturás por mes, aproximadamente? (USD) *
              </Label>
              {renderRadioOptions(
                "monthlyRevenue",
                [
                  { value: "USD 0 - 10k", label: "Menos de USD 10.000" },
                  { value: "USD 10k - 25k", label: "USD 10.000 - 25.000" },
                  { value: "USD 25k - 100k", label: "USD 25.000 - 100.000" },
                  { value: "USD 100k+", label: "Más de USD 100.000" },
                ],
                monthlyRevenue,
                setMonthlyRevenue,
                true,
                5,
              )}
            </div>
          )}

          {/* STEP 6 — Datos: Nombre, Apellido, Empresa */}
          {step === 6 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="mb-6">
                <Label className="text-lg font-medium mb-1 block">
                  Casi listo
                </Label>
                <p className="text-sm text-muted-foreground">
                  Necesitamos unos datos para poder llamarte.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    name="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="h-12 bg-card"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Apellido *</Label>
                  <Input
                    name="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="h-12 bg-card"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nombre de tu empresa *</Label>
                <Input
                  name="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="h-12 bg-card"
                />
              </div>
            </div>
          )}

          {/* STEP 7 — Teléfono y Mail */}
          {step === 7 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="mb-6">
                <Label className="text-lg font-medium mb-1 block">
                  ¿Dónde te llamamos?
                </Label>
                <p className="text-sm text-muted-foreground">
                  En minutos te escribimos por WhatsApp para coordinar la
                  llamada.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Teléfono (WhatsApp) *</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+54 9 11 1234-5678"
                  required
                  className="h-12 bg-card"
                />
              </div>
              <div className="space-y-2">
                <Label>Mail *</Label>
                <Input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 bg-card"
                />
              </div>
            </div>
          )}

          {/* STEP 8 — Confirmación */}
          {step === 8 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300 text-center py-8">
              <div className="bg-primary/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/20">
                <Check className="w-10 h-10 text-primary" />
              </div>
              <Label className="text-2xl font-medium mb-2 block">
                Listo, {firstName}.
              </Label>
              <p className="text-muted-foreground mb-2">
                Revisá tus datos y confirmá:
              </p>
              <div className="bg-card border border-white/10 rounded-xl p-5 text-left space-y-2 text-sm">
                <p>
                  <span className="text-muted-foreground">Nombre:</span>{" "}
                  {firstName} {lastName}
                </p>
                <p>
                  <span className="text-muted-foreground">Empresa:</span>{" "}
                  {companyName}
                </p>
                <p>
                  <span className="text-muted-foreground">WhatsApp:</span>{" "}
                  {phone}
                </p>
                <p>
                  <span className="text-muted-foreground">Mail:</span> {email}
                </p>
                <p>
                  <span className="text-muted-foreground">Negocio:</span>{" "}
                  {businessType === "servicio" ? "Servicio" : "Productos"}
                </p>
                <p>
                  <span className="text-muted-foreground">Estado:</span>{" "}
                  {businessStatus}
                </p>
                <p>
                  <span className="text-muted-foreground">Para cuándo:</span>{" "}
                  {whenToStart}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-8 pt-6 border-t border-white/5">
          {step > 1 && !(step === 2 && businessType === "descarte") ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(step - 1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Atrás
            </Button>
          ) : (
            <div></div>
          )}

          {step < STEPS ? (
            <Button
              type="button"
              onClick={handleContinue}
              disabled={!canContinue()}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Siguiente
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  Confirmar y ver video
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
